/**
 * =========================================================
 * features/ads/api.ts — Ads API layer
 * =========================================================
 * Qëllimi: Thirrjet API per reklamat (publike + admin).
 * Endpoint: GET /api/v1/ads/active
 *           POST /api/v1/ads/track-click
 *           /api/v1/superadmin/ads/* (superadmin)
 * =========================================================
 */
import { apiClient } from "@/lib/api/client";
import { asArray, unwrapApiData } from "@/lib/api/response";

export interface AdCreativeOut {
  id:           number;
  title:        string;
  image_url:    string;
  click_url:    string;
  cta_text:     string;
  domain_label: string | null;
  placement:    string;
  priority:     number;
  offer_price?: number | null;
  offer_currency?: string | null;
}

export interface AdAdminOut extends AdCreativeOut {
  is_active:    boolean;
  clicks_count: number;
  impressions_count?: number;
  starts_at:    string | null;
  ends_at:      string | null;
  created_at:   string;
  business_account_id?: number | null;
  business_name?: string | null;
  owner_email?: string | null;
  is_platform_internal?: boolean;
  invoice_id?: number | null;
  invoice_number?: string | null;
  invoice_status?: string | null;
  invoice_total_amount?: number | null;
  manual_invoice_id?: number | null;
  manual_invoice_token?: string | null;
  billing_mode?: "platform_free" | "owner_invoice" | "manual_invoice";
  promo_ribbon_item_id?: number | null;
  ribbon_bg_color?: string | null;
  ribbon_text_color?: string | null;
}

export interface AdCreateIn {
  title:               string;
  image_url?:          string | null;
  storage_key?:        string | null;
  click_url:           string;
  cta_text?:           string;
  domain_label?:       string;
  placement:           string;
  priority?:           number;
  business_account_id?: number | null;
  is_platform_internal?: boolean;
  billing_mode?: "platform_free" | "owner_invoice" | "manual_invoice";
  manual_invoice_recipient_name?: string | null;
  manual_invoice_recipient_email?: string | null;
  ribbon_bg_color?: string | null;
  ribbon_text_color?: string | null;
  days?:               number;
}

export interface AdUpdateIn {
  title?:        string;
  image_url?:    string | null;
  storage_key?:  string | null;
  click_url?:    string;
  cta_text?:     string;
  domain_label?: string | null;
  placement?:    string;
  priority?:     number;
  is_active?:    boolean;
  starts_at?:    string | null;
  ends_at?:      string | null;
  ribbon_bg_color?: string | null;
  ribbon_text_color?: string | null;
}

export interface AdRequestIn {
  business_id?: number | null;
  source_post_id?: number | null;
  title: string;
  image_url?: string | null;
  storage_key?: string | null;
  click_url: string;
  cta_text?: string;
  domain_label?: string | null;
  placement: string;
  priority?: number;
  days?: number;
  offer_price?: number | null;
  offer_currency?: string | null;
}

export interface AdRequestOut {
  id: number;
  status: string;
  message: string;
  quote_total_eur?: number | null;
  invoice_id?: number | null;
  invoice_status?: string | null;
}

export interface BusinessAdOut {
  id: number;
  source: "request" | "creative";
  title: string;
  image_url?: string | null;
  click_url: string;
  cta_text: string;
  placement: string;
  priority: number;
  status: string;
  invoice_id?: number | null;
  invoice_status?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
  clicks_count: number;
  impressions_count: number;
}

export interface BusinessAdUpdateIn {
  title: string;
  click_url: string;
  cta_text: string;
  placement: string;
  priority: number;
}

export interface AdRequestAdminOut {
  id: number;
  title: string;
  placement: string;
  status: string;
  days: number;
  priority: number;
  business_id?: number | null;
  business_name?: string | null;
  requester_email?: string | null;
  owner_email?: string | null;
  quote_total_eur?: number | null;
  invoice_id?: number | null;
  invoice_status?: string | null;
  invoice_total_amount?: number | null;
  requested_at?: string | null;
}

export interface PricingQuoteOut {
  placement_scope: string;
  days:            number;
  base_price_eur:  number;
  tax_rate:        number;
  tax_amount:      number;
  final_price_eur: number;
  bundle:          boolean;
}

export interface AdOwnerOption {
  id: number;
  full_name: string;
  email: string | null;
}

export const getActiveAds = async (placement: string): Promise<AdCreativeOut[]> => {
  const res = await apiClient.get<unknown>("/ads/active", {
    params: { placement, limit: 5 },
  });
  return asArray<AdCreativeOut>(res.data);
};

export const trackAdClick = async (
  creative_id: number,
  placement: string,
  user_id?: number,
): Promise<void> => {
  await apiClient.post("/ads/track-click", { creative_id, placement, user_id });
};

export const trackAdImpression = async (
  creative_id: number,
  placement: string,
  user_id?: number,
): Promise<void> => {
  await apiClient.post("/ads/track-impression", { creative_id, placement, user_id });
};

export const getAdminAds = async (): Promise<AdAdminOut[]> => {
  const res = await apiClient.get<unknown>("/superadmin/ads/creatives");
  return asArray<AdAdminOut>(res.data);
};

export const getAdOwnerOptions = async (q?: string): Promise<AdOwnerOption[]> => {
  const res = await apiClient.get<unknown>("/superadmin/ad-owner-options", {
    params: q ? { q } : undefined,
  });
  return asArray<AdOwnerOption>(res.data);
};

export interface AdCreateResult {
  ok: boolean;
  id: number;
  message?: string;
  invoice_id?: number | null;
  invoice_number?: string | null;
  invoice_business_id?: number | null;
  invoice_total_amount?: number | null;
  total_eur?: number | null;
  is_platform_internal?: boolean;
  manual_invoice_id?: number | null;
  manual_invoice_token?: string | null;
  billing_mode?: "platform_free" | "owner_invoice" | "manual_invoice";
}

export const createAd = async (payload: AdCreateIn): Promise<AdCreateResult> => {
  const { days = 7, ...body } = payload;
  const res = await apiClient.post<unknown>("/superadmin/ads/creatives", body, { params: { days } });
  return unwrapApiData<AdCreateResult>(res.data);
};

export const requestAd = async (payload: AdRequestIn): Promise<AdRequestOut> => {
  const { business_id, days = 7, ...body } = payload;
  const res = await apiClient.post<unknown>(
    "/ads/requests",
    { ...body, days },
    business_id ? { params: { business_id } } : undefined,
  );
  return unwrapApiData<AdRequestOut>(res.data);
};

const businessRequestConfig = (businessId: number) => ({
  headers: { "X-Business-Id": String(businessId) },
});

export const getMyBusinessAds = async (businessId: number): Promise<BusinessAdOut[]> => {
  const res = await apiClient.get<BusinessAdOut[]>("/ads/my", businessRequestConfig(businessId));
  return res.data;
};

export const updateMyBusinessAd = async (businessId: number, creativeId: number, payload: BusinessAdUpdateIn): Promise<void> => {
  await apiClient.patch(`/ads/my/creatives/${creativeId}`, payload, businessRequestConfig(businessId));
};

export const updateMyAdRequest = async (businessId: number, requestId: number, payload: BusinessAdUpdateIn): Promise<void> => {
  await apiClient.patch(`/ads/requests/${requestId}`, payload, businessRequestConfig(businessId));
};

export const deleteMyBusinessAd = async (businessId: number, creativeId: number): Promise<void> => {
  await apiClient.delete(`/ads/my/creatives/${creativeId}`, businessRequestConfig(businessId));
};

export const cancelMyAdRequest = async (businessId: number, requestId: number): Promise<void> => {
  await apiClient.delete(`/ads/requests/${requestId}`, businessRequestConfig(businessId));
};

export const getAdminAdRequests = async (status?: string): Promise<AdRequestAdminOut[]> => {
  const res = await apiClient.get<unknown>("/superadmin/ads/requests", {
    params: status ? { status } : undefined,
  });
  return asArray<AdRequestAdminOut>(res.data);
};

export const approveAdRequest = async (requestId: number): Promise<{ ok: boolean; creative_id?: number | null; invoice_id?: number | null; message?: string }> => {
  const res = await apiClient.post<unknown>(`/superadmin/ads/requests/${requestId}/approve`);
  return unwrapApiData<{ ok: boolean; creative_id?: number | null; invoice_id?: number | null; message?: string }>(res.data);
};

export const getPricingQuote = async (
  placementScope: string,
  days: number,
): Promise<PricingQuoteOut> => {
  const res = await apiClient.get<unknown>("/admin/pricing/quote", {
    params: { placement_scope: placementScope, days },
  });
  return unwrapApiData<PricingQuoteOut>(res.data);
};

export const toggleAd = async (id: number, is_active: boolean): Promise<void> => {
  await apiClient.patch(`/superadmin/ads/creatives/${id}`, { is_active });
};

export const updateAd = async (id: number, payload: AdUpdateIn): Promise<void> => {
  await apiClient.patch(`/superadmin/ads/creatives/${id}`, payload);
};

export const deleteAd = async (id: number): Promise<void> => {
  await apiClient.delete(`/superadmin/ads/creatives/${id}`);
};

/**
 * =========================================================
 * Fund i features/ads/api.ts
 * =========================================================
 */
